/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/core/styles/makeStyles";
import Card from "@material-ui/core/Card";
// import CancelIcon from "@material-ui/icons/Cancel";
import AddIcon from "@material-ui/icons/Add";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

// jaza-soft
import { PageHeader, translate, BackButton, PageFooter, showNotification, Button } from "jazasoft";

// local
import MuiTable from "../../../components/MuiTable";
import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";

// helper
import { getDistinctValues, isEmpty, isTrue } from "../../../utils/helpers";
import { format } from "../../../utils/dateUtils";

import { useDeepEffect, useDeepMemo, useEntityPrivilege, useMutation, useQuery, useSafeState, useSetting, useTime } from "../../../hooks";

import SettingKeys from "../../../utils/SettingKeys";

import SizeBreakup from "./SizeBreakup";
import useUnit from "../../../hooks/useUnit";
import CardHeader from "../../../components/CardHeader";
import { getFpoList } from "../../../helpers/orderHelper";
import {
  // colorOrange,
  colorTomatoRed
} from "../../../utils/colors";
import FPOCreateDrawer from "./FPOCreateDrawer";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";

const useStyles = makeStyles(theme => ({
  container: {
    margin: theme.spacing(3),
    marginTop: 0
  },
  card: {
    padding: 0,
    boxShadow: theme.shadows[1],
    backgroundColor: "white",
    borderRadius: 4
  },
  mrTop: {
    marginTop: "1.5em"
  },
  padding: {
    padding: "1.5em"
  },
  footer: {
    marginLeft: 24,
    "& > *": {
      marginRight: "1em"
    }
  }
}));

const OrderPlanning = props => {
  const classes = useStyles();
  const { id, translate, location, roles, hasAccess } = props;
  const resource = "orders";

  const [version, setVersion] = useSafeState(0);
  const [selectedKey, setSelectedKey] = useSafeState();
  const [tabIdx, setTabIdx] = useSafeState(0); // Tabs: Planning, Order
  const [drawerState, setDrawerState] = useSafeState({ open: false });

  const { utc_offset } = useTime();
  const [mutate] = useMutation();
  const dispatch = useDispatch();
  const { disableCreate, disableDelete } = useEntityPrivilege("FPO");

  const { data: order = {} } = useQuery(id && { url: `orders/${id}`, params: { view: "deep" }, version });

  const enbaleExternalFpoMerge = useSetting(SettingKeys.ENABLE_EXTERNAL_FPO_MERGE);
  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const fields = [...orderFields, ...flowInfoFields, "fpo", "fpoQty", "unit"];
  const enbaleExtFpoMerge = isTrue(enbaleExternalFpoMerge);

  const { allUnitList, unitChoices } = useUnit(order);

  const poInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];
    return [{ ...order, customerName: order?.customer?.name, seasonName: order?.season?.name }];
  }, [order]);

  const poInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerPoRef", title: "Customer PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "customField1", title: "Custom Field 1" },
      { dataKey: "customField2", title: "Custom Field 2" },
      { dataKey: "customField3", title: "Custom Field 3" },
      { dataKey: "desc", title: "Remarks" },
      { dataKey: "fpoLevel", title: "FPO Level" }
    ];
    cols = cols.filter(c => orderFields.includes(c.dataKey) || c.dataKey === "fpoLevel");
    return cols;
  }, [orderFields]);

  const rows = useDeepMemo(() => {
    if (tabIdx === 0) {
      const fpoList = getFpoList(order);
      return fpoList.flatMap(row => {
        let children = [];
        if (row.flowInfoList.length > 1) {
          children = row.flowInfoList
            .map(fi => ({
              ...fi,
              colorId: `${fi.product?.id}-${fi.style}-${fi.color}`,
              key: fi.id,
              parentKey: row.fpo,
              productName: fi.product?.name,
              unit: allUnitList?.find(u => u.unitId === fi.primaryUnitId)?.name
            }))
            .sort((a, b) => (a.colorId !== b.colorId ? (a.colorId < b.colorId ? -1 : 1) : a.serialNo - b.serialNo));
        }
        return [
          {
            ...row,
            status: isTrue(row.cancelled) ? "Cancelled" : "-",
            unit: allUnitList?.find(u => u.unitId === row.unitId)?.name
          },
          ...children
        ];
      });
    } else {
      return order.flowInfoList
        .filter(fi => isEmpty(fi.orderParentId))
        .sort((a, b) => a.serialNo - b.serialNo)
        .map((fi, idx) => ({
          ...fi,
          sortField: `${fi.product?.id}-${fi.style}-${fi.color}-${isEmpty(fi.parentId) ? "-0" : `-${idx + 1}`}`,
          productName: fi.product?.name,
          key: fi.id,
          parentKey: fi.parentId,
          deliveryDate: utc_offset && !isEmpty(fi.exFactoryDate) ? format(fi.exFactoryDate, utc_offset, "dd MMM yy") : null
        }))
        .sort((a, b) => (a.sortField < b.sortField ? -1 : 1));
    }
  }, [tabIdx, order, allUnitList]);

  const hasExternalFpo = rows.some(e => !isEmpty(e.externalFpo));

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "fpo", title: "FPO", length: 100, render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "externalFpo", title: "Ext. FPO", length: 100, render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "unit", title: "Unit", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", highlight: true },
      { dataKey: "styleNo", title: "Style No", highlight: true },
      { dataKey: "style", title: "Style", highlight: true },
      { dataKey: "color", title: "Color", highlight: true },
      { dataKey: "inseam", title: "Inseam", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "destination", title: "Destination", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "BPO", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "flowRef", title: "Line ID", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "delFlowRef", title: "Del. Line ID", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "extra", title: "Extra(%)" },
      { dataKey: "fpoExtra", title: "Extra Cut(%)" },
      { dataKey: "status", title: "Status" }
    ];
    if (!hasExternalFpo) {
      cols = cols.filter(c => c.dataKey !== "externalFpo");
    }
    if (tabIdx === 0) {
      cols = cols.filter(c => !["orderQty", "extra"].includes(c.dataKey));
    } else {
      cols = cols.filter(c => !["fpo", "externalFpo", "unit", "fpoQty", "fpoExtra"].includes(c.dataKey));
    }

    return cols.filter(c => [...fields, "externalFpo", "fpoExtra"].includes(c.dataKey));
  }, [tabIdx, fields, hasExternalFpo]);

  const routeState = location.state;
  useDeepEffect(() => {
    if (isEmpty(rows)) return;
    if (tabIdx === 0) {
      if (!isEmpty(routeState?.fpo)) {
        setSelectedKey(rows.find(e => isEmpty(e.parentKey) && e.fpo === routeState.fpo)?.key || rows[0]?.key);
      } else {
        setSelectedKey(rows[0]?.key);
      }
    } else {
      setSelectedKey(rows[0]?.key);
    }
  }, [rows, tabIdx, routeState]);

  const onRowClick = row => {
    if (tabIdx === 0) {
      if (isEmpty(row.parentKey)) {
        setSelectedKey(row.key);
      }
    } else {
      setSelectedKey(row.key);
    }
  };

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    const flowInfoList = selectedRows.filter(e => isEmpty(e.parentKey)).map(e => ({ fpo: e.fpo }));
    if (action === "merge") {
      if (flowInfoList.length < 2) {
        dispatch(showNotification(`Select at least 2 FPO to merge.`, { type: "danger" }));
        return;
      }
    } else {
      const flowInfoList = selectedRows.filter(e => isEmpty(e.parentKey)).map(e => ({ fpo: e.fpo }));
      if (flowInfoList.length !== 1 || !flowInfoList[0].fpo?.includes(",")) {
        dispatch(showNotification(`Select only one merged FPO for splitting.`, { type: "danger" }));
        return;
      }
    }

    const options = {
      url: "fpo",
      method: "patch",
      params: { orderId: order?.id, action },
      data: { flowInfoList }
    };
    mutate(options, {
      onSuccess: () => {
        if (action === "merge") {
          dispatch(showNotification(`FPO merged successfully.`));
        } else {
          dispatch(showNotification(`FPO split successfully.`));
        }
        setVersion(v => v + 1);
        onActionComplete && onActionComplete(selectedRows);
      }
    });
  };

  const onInlineActionClick = (e, name, row, onActionComplete) => {
    const options = {
      url: `fpo`,
      method: "delete",
      params: { orderId: order?.id, fpo: row.fpo, action: name === "delete" ? "delete" : "cancel" }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification(`FPO ${name === "delete" ? "deleted" : "cancelled"} successfully.`));
        setVersion(v => v + 1);
        onActionComplete && onActionComplete(row);
      }
    });
  };

  const parentKeys = getDistinctValues(rows?.filter(row => !isEmpty(row.parentKey)).map(row => row.parentKey));
  const defaultExpanded = parentKeys?.length <= 1;

  const selectedRow = rows.find(e => e.key === selectedKey);

  const selectActions = [
    {
      name: "split",
      tooltip: "Split",
      showLabel: true,
      options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 120 } }
    },
    {
      name: "merge",
      tooltip: "Merge",
      showLabel: true,
      options: { variant: "outlined", color: "primary", style: { marginLeft: "1em", width: 150 } }
    }
  ];

  let inlineActions = [];
  if (tabIdx === 0 && !disableDelete && hasPrivilege(roles, hasAccess, ResourceId.FPO, Scope.DELETE)) {
    inlineActions = row => {
      if (row.parentKey) return [];
      // if (row.cancelled) return [{ name: "delete", tooltip: "Delete", options: { style: { color: colorTomatoRed.dark } } }];
      return [
        // { name: "cancel_fpo", tooltip: "Cancel", icon: <CancelIcon />, options: { style: { color: colorOrange.dark } } },
        { name: "delete", tooltip: "Delete", options: { style: { color: colorTomatoRed.dark } } }
      ];
    };
  }

  return (
    <div>
      <PageHeader title="resources.orders.pageHeader.view" id={id} titleArgs={{ _: `Order #${id}` }}>
        {!disableCreate && hasPrivilege(roles, hasAccess, ResourceId.FPO, Scope.WRITE) && (
          <Button label="Create FPO" variant="contained" color="primary" onClick={() => setDrawerState({ open: true })}>
            <AddIcon />
          </Button>
        )}
      </PageHeader>

      <FPOCreateDrawer
        open={drawerState?.open || false}
        order={order}
        unitList={unitChoices}
        onDrawerClose={() => setDrawerState({ open: false })}
        onRefresh={() => setVersion(v => v + 1)}
      />

      <div className={classes.container}>
        {/* Order Summary */}
        <MuiTable
          title={<CardHeader style={{ padding: 0 }} title={translate(`resources.orders.cardHeader.summary`, { _: "Order Summary" })} />}
          toolbar
          columns={poInfoColumns}
          rows={poInfoRows}
          i18nKey="resources.orders.fields"
        />

        <div className={classes.mrTop}>
          <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
            <SubTab label={translate("resources.fpo.name", { _: "FPO", smart_count: 1 })} />
            <SubTab label={translate("resources.orders.cardHeader.detail", { _: "Order Details" })} />
          </Tabs>

          {/* Order Details */}
          <Card className={classes.mrTop}>
            <PerfectScrollbar>
              <MuiTable
                key={JSON.stringify(rows)}
                title={
                  tabIdx === 0
                    ? translate("resources.fpo.name", { _: "FPO", smart_count: 3 })
                    : translate("resources.orders.cardHeader.detail", { _: "Order Details" })
                }
                columns={columns}
                rows={rows}
                searchable
                searchKeys={
                  tabIdx === 0
                    ? ["fpo", "styleNo", "style", "flowRef", "delFlowRef", "customerFlowRef"]
                    : ["styleNo", "style", "flowRef", "delFlowRef", "customerFlowRef"]
                }
                selectAll={false}
                selectable={row => tabIdx === 0 && hasExternalFpo && enbaleExtFpoMerge && isEmpty(row.parentKey)}
                idKey="key"
                parentIdKey="parentKey"
                defaultExpanded={defaultExpanded}
                onRowClick={onRowClick}
                selectActions={selectActions}
                onSelectActionClick={onSelectActionClick}
                inlineActions={inlineActions}
                onInlineActionClick={onInlineActionClick}
                rowStyle={({ row }) => (selectedKey === row?.key ? { backgroundColor: "#BFDDF8" } : {})}
                i18nKey="resources.orders.fields.flowInfo"
              />
            </PerfectScrollbar>
          </Card>
        </div>

        <SizeBreakup view={tabIdx === 0 ? "fpo" : "order"} order={order} selectedRow={selectedRow} />
      </div>

      <PageFooter className={classes.footer}>
        <BackButton variant="outlined" color="primary" />
      </PageFooter>
    </div>
  );
};

export default translate(OrderPlanning);
